import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Bug } from 'lucide-react';

const TransactionsModal = ({ transactionsData, bankAccount, bankName, currency, subsidiary, runId, workflowId, netsuiteAccountId, onClose, onConfirm, onRefresh, isLiveWorkflow }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all'); // all, bank_agent, bill_agent, je_agent
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [transferringIndex, setTransferringIndex] = useState(null);

  // Transfer to Customer/Vendor state
  const [transferringEntityIndex, setTransferringEntityIndex] = useState(null);
  const [transferringEntityType, setTransferringEntityType] = useState(null); // 'customer' or 'vendor'

  if (!transactionsData) {
    return null;
  }

  // Build NetSuite URL based on account_id
  const getNetSuiteUrl = (recordType, recordId) => {
    // Replace underscores with hyphens for NetSuite URL format
    const formattedAccountId = netsuiteAccountId ? netsuiteAccountId.replace(/_/g, '-') : '';
    const baseUrl = formattedAccountId
      ? `https://${formattedAccountId}.app.netsuite.com`
      : 'https://system.netsuite.com';

    const recordPaths = {
      'custpymt': 'app/accounting/transactions/custpymt.nl',
      'vendpymt': 'app/accounting/transactions/vendpymt.nl',
      'journal': 'app/accounting/transactions/journal.nl',
      'customer': 'app/common/entity/custjob.nl',
      'vendor': 'app/common/entity/vendor.nl'
    };

    const path = recordPaths[recordType] || `app/accounting/transactions/${recordType}.nl`;
    return `${baseUrl}/${path}?id=${recordId}`;
  };

  // Handle transfer single transaction to JE
  const handleTransferToJE = async (transaction, index) => {
    if (!runId) return;

    const txIndex = transaction.transaction_index !== undefined ? transaction.transaction_index : index;
    const confirmMsg = `Transfer this transaction to Journal Entry?\n\nAmount: ${(transaction.Amount || transaction.amount || 0).toLocaleString()}\nDescription: ${transaction.description || 'N/A'}`;
    if (!window.confirm(confirmMsg)) return;

    setTransferringIndex(txIndex);
    const token = Cookies.get('sankalvax_token');

    try {
      const response = await fetch(`/api/bank/v2/workflows/transfer-to-je`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          parent_run_id: runId,
          transaction_index: txIndex
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('✅ Successfully transferred to Journal Entry!');
        if (onRefresh) onRefresh();
      } else {
        alert(`❌ Transfer failed: ${data.detail || data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Transfer to JE error:', error);
      alert(`❌ Transfer failed: ${error.message}`);
    } finally {
      setTransferringIndex(null);
    }
  };

  // Handle transfer to Customer/Vendor (like transfer to JE)
  const handleTransferToEntity = async (transaction, index, entityType) => {
    if (!runId) return;

    const txIndex = transaction.transaction_index !== undefined ? transaction.transaction_index : index;
    const noun = entityType === 'vendor' ? 'Vendor' : 'Customer';
    const confirmMsg = `Transfer this transaction to ${noun} Agent?\n\nAmount: ${(transaction.Amount || transaction.amount || 0).toLocaleString()}\nDescription: ${transaction.description || 'N/A'}\n\nYou will select the ${noun.toLowerCase()} in the chat screen.`;
    if (!window.confirm(confirmMsg)) return;

    setTransferringEntityIndex(txIndex);
    setTransferringEntityType(entityType);
    const token = Cookies.get('sankalvax_token');

    try {
      const response = await fetch(`/api/bank/v2/workflows/transfer-entity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          parent_run_id: runId,
          transaction_index: txIndex,
          entity_type: entityType
        })
      });

      const data = await response.json();
      if (data.success) {
        alert(`✅ Successfully transferred to ${noun} Agent! Click "Approve" to select the ${noun.toLowerCase()}.`);
        if (onRefresh) onRefresh();
      } else {
        alert(`❌ Transfer failed: ${data.detail || data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Transfer entity error:', error);
      alert(`❌ Transfer failed: ${error.message}`);
    } finally {
      setTransferringEntityIndex(null);
      setTransferringEntityType(null);
    }
  };

  const getFilteredTransactions = () => {
    const classifications = transactionsData.classifications || [];
    if (filter === 'all') return classifications;
    return classifications.filter(t => t.agent_type === filter);
  };

  const getAgentColor = (agentType) => {
    switch (agentType) {
      case 'bank_agent': return '#10b981'; // green
      case 'bill_agent': return '#f59e0b'; // orange
      case 'je_agent': return '#6366f1'; // indigo
      default: return '#6b7280'; // gray
    }
  };

  const getAgentLabel = (agentType) => {
    switch (agentType) {
      case 'bank_agent': return 'Customer Payment (AR)';
      case 'bill_agent': return 'Vendor Payment (AP)';
      case 'je_agent': return 'Journal Entry';
      default: return agentType;
    }
  };

  // Handle debug navigation for a transaction
  const handleDebug = (transaction) => {
    const params = new URLSearchParams();
    if (runId) params.set('parentRun', runId);
    if (transaction.child_run_id) params.set('childRun', transaction.child_run_id);
    // Pass returnTo so debug page can navigate back to TransactionsModal
    params.set('returnTo', 'transactions');
    params.set('workflowId', workflowId || '');
    navigate(`/bank-debug?${params.toString()}`);
  };

  const toggleRowExpand = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  // Handle approve transaction - redirect to BankAgentChat
  const handleApproveTransaction = (transaction) => {
    console.log('🔵 Approving transaction:', transaction);

    // Encode transaction data for URL
    const transactionData = encodeURIComponent(JSON.stringify(transaction));

    // Navigate to BankAgentChat with transaction data and parent workflow info
    const params = new URLSearchParams({
      transaction: transactionData,
      parentRun: runId,
      ...(workflowId && { parentWorkflow: workflowId })
    });

    navigate(`/bank-agent-chat?${params.toString()}`);
  };

  const filteredTransactions = getFilteredTransactions();
  const stats = transactionsData || {};
  const allClassifications = transactionsData.classifications || [];

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl w-[98%] max-w-[1800px] max-h-[90vh] flex flex-col shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-200 flex justify-between items-start bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
          <div>
            <h2 className="m-0 mb-2 text-2xl font-semibold">Classified Bank Transactions</h2>
            <div className="flex gap-6 text-sm font-medium text-white items-center">
              <span className="flex gap-1"><strong>Bank:</strong> {bankName || bankAccount || 'N/A'}</span>
              <span className="flex gap-1"><strong>Currency:</strong> {currency || 'N/A'}</span>
              <span className="flex gap-1"><strong>Subsidiary:</strong> {subsidiary || 'N/A'}</span>
              {runId && (
                <button
                  onClick={async () => {
                    try {
                      const token = Cookies.get('sankalvax_token');

                      // Get the file first to check its type
                      const response = await fetch(`/api/bank/v2/workflows/view-file/${runId}`, {
                        headers: {
                          'Authorization': `Bearer ${token}`
                        }
                      });

                      if (!response.ok) {
                        console.error('Failed to fetch file:', response.status);
                        alert('Failed to load file');
                        return;
                      }

                      const contentType = response.headers.get('content-type');
                      const blob = await response.blob();
                      const blobUrl = window.URL.createObjectURL(blob);

                      // For PDFs, open directly
                      if (contentType && contentType.includes('pdf')) {
                        window.open(blobUrl, '_blank');
                        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
                      } else {
                        // For Excel files, use Google Docs Viewer
                        const fileUrl = `${window.location.origin}/api/bank/v2/workflows/view-file/${runId}?token=${token}`;
                        const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;
                        window.open(viewerUrl, '_blank');
                        window.URL.revokeObjectURL(blobUrl);
                      }
                    } catch (error) {
                      console.error('Error viewing file:', error);
                      alert('Error loading file');
                    }
                  }}
                  className="ml-4 px-3 py-1.5 bg-indigo-500 text-white border-none rounded-md cursor-pointer text-sm font-medium hover:bg-indigo-600 transition-colors"
                >
                  📄 View PDF
                </button>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="bg-white/20 border-none text-white text-3xl w-10 h-10 rounded-full cursor-pointer flex items-center justify-center transition-all hover:bg-white/30 hover:scale-110"
          >
            &times;
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 px-8 py-6 bg-gray-50 border-b border-gray-200">
          <div className="bg-white p-4 rounded-lg border-2 border-gray-200 text-center">
            <div className="text-xs text-gray-500 uppercase font-semibold mb-2">Total Transactions</div>
            <div className="text-3xl font-bold text-gray-900">{stats.totallines || 0}</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg border-2 border-red-400 text-center">
            <div className="text-xs text-gray-500 uppercase font-semibold mb-2">Debit Count</div>
            <div className="text-3xl font-bold text-gray-900">{stats.totaldebitcount || 0}</div>
            <div className="text-sm text-gray-500 mt-1 font-medium">{stats.totaldebitamount || '0.00'}</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border-2 border-green-400 text-center">
            <div className="text-xs text-gray-500 uppercase font-semibold mb-2">Credit Count</div>
            <div className="text-3xl font-bold text-gray-900">{stats.totalcreditcount || 0}</div>
            <div className="text-sm text-gray-500 mt-1 font-medium">{stats.totalcreditamount || '0.00'}</div>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-3 px-8 py-4 bg-white border-b border-gray-200">
          <button
            className={`px-4 py-2 border-2 rounded-md cursor-pointer font-medium transition-all text-sm ${
              filter === 'all'
                ? 'bg-indigo-500 text-white border-indigo-500'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => setFilter('all')}
          >
            All ({allClassifications.length})
          </button>
          <button
            className={`px-4 py-2 border-2 rounded-md cursor-pointer font-medium transition-all text-sm ${
              filter === 'bank_agent'
                ? 'bg-indigo-500 text-white border-indigo-500'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => setFilter('bank_agent')}
            style={{ borderColor: filter !== 'bank_agent' ? getAgentColor('bank_agent') : undefined }}
          >
            AR ({allClassifications.filter(t => t.agent_type === 'bank_agent').length})
          </button>
          <button
            className={`px-4 py-2 border-2 rounded-md cursor-pointer font-medium transition-all text-sm ${
              filter === 'bill_agent'
                ? 'bg-indigo-500 text-white border-indigo-500'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => setFilter('bill_agent')}
            style={{ borderColor: filter !== 'bill_agent' ? getAgentColor('bill_agent') : undefined }}
          >
            AP ({allClassifications.filter(t => t.agent_type === 'bill_agent').length})
          </button>
          <button
            className={`px-4 py-2 border-2 rounded-md cursor-pointer font-medium transition-all text-sm ${
              filter === 'je_agent'
                ? 'bg-indigo-500 text-white border-indigo-500'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => setFilter('je_agent')}
            style={{ borderColor: filter !== 'je_agent' ? getAgentColor('je_agent') : undefined }}
          >
            JE ({allClassifications.filter(t => t.agent_type === 'je_agent').length})
          </button>
        </div>

        {/* Transactions Table */}
        <div className="flex-1 overflow-y-auto px-8 pb-6">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 bg-gray-50 z-10">
              <tr>
                <th className="p-3 text-left font-semibold text-gray-700 border-b-2 border-gray-200 text-xs uppercase w-[50px]">Expand</th>
                <th className="p-3 text-left font-semibold text-gray-700 border-b-2 border-gray-200 text-xs uppercase">#</th>
                <th className="p-3 text-left font-semibold text-gray-700 border-b-2 border-gray-200 text-xs uppercase">Date</th>
                <th className="p-3 text-left font-semibold text-gray-700 border-b-2 border-gray-200 text-xs uppercase">Description</th>
                <th className="p-3 text-left font-semibold text-gray-700 border-b-2 border-gray-200 text-xs uppercase">Type</th>
                <th className="p-3 text-left font-semibold text-gray-700 border-b-2 border-gray-200 text-xs uppercase">Amount</th>
                <th className="p-3 text-left font-semibold text-gray-700 border-b-2 border-gray-200 text-xs uppercase">Classification</th>
                <th className="p-3 text-left font-semibold text-gray-700 border-b-2 border-gray-200 text-xs uppercase">Status</th>
                <th className="p-3 text-left font-semibold text-gray-700 border-b-2 border-gray-200 text-xs uppercase">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center p-10 text-gray-500">
                    No transactions found for this filter
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((transaction, index) => (
                  <React.Fragment key={index}>
                    {/* Main Row */}
                    <tr className="border-b border-gray-200 transition-colors hover:bg-gray-50 cursor-pointer">
                      <td className="p-3 text-gray-700">
                        <button
                          onClick={() => toggleRowExpand(index)}
                          className="cursor-pointer bg-transparent border-none text-lg text-indigo-500 hover:text-purple-600 hover:scale-125 transition-all"
                        >
                          {expandedRows.has(index) ? '▼' : '▶'}
                        </button>
                      </td>
                      <td className="p-3 text-gray-700">{transaction.transaction_index + 1}</td>
                      <td className="p-3 text-gray-700">{transaction.date || transaction.trandate}</td>
                      <td className="p-3 text-gray-700 max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap">{transaction.description}</td>
                      <td className="p-3 text-gray-700">
                        <span className={`inline-block px-2 py-1 rounded text-[11px] font-semibold uppercase ${
                          transaction.transactiontype === 'debit'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {transaction.transactiontype}
                        </span>
                      </td>
                      <td className="p-3 text-gray-700 font-semibold font-mono">{transaction.amount || transaction.Amount}</td>
                      <td className="p-3 text-gray-700">
                        <span
                          className="inline-block px-3 py-1 rounded-xl text-[11px] font-semibold text-white whitespace-nowrap"
                          style={{ backgroundColor: getAgentColor(transaction.agent_type) }}
                        >
                          {getAgentLabel(transaction.agent_type)}
                        </span>
                      </td>
                      <td className="p-3 text-gray-700">
                        {(() => {
                          // Check if payment has been created (must check array length, not just truthy)
                          const hasPayment = !!(
                            (transaction.ns_payment_id?.length > 0) ||
                            (transaction.ns_billpayment_id?.length > 0) ||
                            (transaction.payment_summary?.payment_ids?.length > 0) ||
                            (transaction.ns_pay_doc?.length > 0) ||
                            (transaction.ns_billpay_doc?.length > 0)
                          );

                          // Check if JE has been created (must check array length)
                          const hasJE = !!((transaction.ns_je_id?.length > 0) || (transaction.ns_JE_id?.length > 0) || transaction.je_internal_id);

                          // Check if mapped based on agent_type
                          const isMapped = transaction.agent_type === 'bill_agent'
                            ? !!transaction.nsvendorinternalid
                            : transaction.agent_type === 'bank_agent'
                            ? !!transaction.nscustomerinternalid
                            : true; // JE agent doesn't need mapping

                          // Determine status and styling
                          let statusText, statusClass;
                          if (hasPayment) {
                            statusText = 'Payment Created';
                            statusClass = 'bg-blue-100 text-blue-800';
                          } else if (hasJE) {
                            statusText = 'JE Created';
                            statusClass = 'bg-purple-100 text-purple-800';
                          } else if (isMapped) {
                            statusText = 'Mapped';
                            statusClass = 'bg-green-100 text-green-800';
                          } else {
                            statusText = 'Unmapped';
                            statusClass = 'bg-red-100 text-red-800';
                          }

                          return (
                            <span className={`inline-block px-2 py-1 rounded text-[11px] font-semibold uppercase ${statusClass}`}>
                              {statusText}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="p-3 text-gray-700">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApproveTransaction(transaction)}
                            className={`px-3 py-1.5 border-none rounded cursor-pointer text-[13px] font-medium transition-colors ${
                              transaction.has_child_workflow || transaction.child_run_id
                                ? 'bg-blue-500 hover:bg-blue-600 text-white'
                                : 'bg-green-500 hover:bg-green-600 text-white'
                            }`}
                          >
                            {transaction.has_child_workflow || transaction.run_id ? '💬 Go to Chat' : '✓ Approve'}
                          </button>
                          {/* Transfer to JE button - only show if not already JE and no payment created */}
                          {transaction.agent_type !== 'je_agent' &&
                           !transaction.ns_billpayment_id?.length &&
                           !transaction.ns_payment_id?.length &&
                           !transaction.payment_summary?.payment_ids?.length && (
                            <button
                              onClick={() => handleTransferToJE(transaction, index)}
                              disabled={transferringIndex === (transaction.transaction_index !== undefined ? transaction.transaction_index : index)}
                              className="px-3 py-1.5 border-none rounded cursor-pointer text-[13px] font-medium transition-colors bg-indigo-500 hover:bg-indigo-600 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                              title="Transfer to Journal Entry"
                            >
                              {transferringIndex === (transaction.transaction_index !== undefined ? transaction.transaction_index : index) ? '...' : '→ JE'}
                            </button>
                          )}
                          {/* Transfer to Customer button - show for all agent types if no payment created */}
                          {!transaction.ns_billpayment_id?.length &&
                           !transaction.ns_payment_id?.length &&
                           !transaction.ns_je_id?.length &&
                           !transaction.payment_summary?.payment_ids?.length && (
                            <button
                              onClick={() => handleTransferToEntity(transaction, index, 'customer')}
                              disabled={transferringEntityIndex === (transaction.transaction_index !== undefined ? transaction.transaction_index : index)}
                              className="px-3 py-1.5 border-none rounded cursor-pointer text-[13px] font-medium transition-colors bg-blue-100 hover:bg-blue-200 text-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:text-white"
                              title="Transfer to Customer Agent"
                            >
                              {transferringEntityIndex === (transaction.transaction_index !== undefined ? transaction.transaction_index : index) && transferringEntityType === 'customer' ? '...' : '→ Cust'}
                            </button>
                          )}
                          {/* Transfer to Vendor button - show for all agent types if no payment created */}
                          {!transaction.ns_billpayment_id?.length &&
                           !transaction.ns_payment_id?.length &&
                           !transaction.ns_je_id?.length &&
                           !transaction.payment_summary?.payment_ids?.length && (
                            <button
                              onClick={() => handleTransferToEntity(transaction, index, 'vendor')}
                              disabled={transferringEntityIndex === (transaction.transaction_index !== undefined ? transaction.transaction_index : index)}
                              className="px-3 py-1.5 border-none rounded cursor-pointer text-[13px] font-medium transition-colors bg-orange-100 hover:bg-orange-200 text-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:text-white"
                              title="Transfer to Vendor Agent"
                            >
                              {transferringEntityIndex === (transaction.transaction_index !== undefined ? transaction.transaction_index : index) && transferringEntityType === 'vendor' ? '...' : '→ Vend'}
                            </button>
                          )}
                          {/* Debug button - show execution logs */}
                          <button
                            onClick={() => handleDebug(transaction)}
                            className="p-1.5 border-none rounded cursor-pointer transition-colors bg-gray-200 hover:bg-gray-300 text-gray-600"
                            title="View Execution Logs"
                          >
                            <Bug className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Details Row */}
                    {expandedRows.has(index) && (
                      <tr className="bg-gray-50">
                        <td colSpan="9" className="p-5">
                          <div>
                            <h4 className="mt-0 mb-4 text-gray-700 font-semibold">Complete Transaction Details</h4>

                            {/* Classification Details */}
                            <div className="mb-5 bg-white p-4 rounded-lg border border-gray-200">
                              <h5 className="m-0 mb-2.5 text-sm font-semibold uppercase tracking-wide text-gray-500">Classification Information</h5>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="flex flex-col gap-1 col-span-2">
                                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Description:</span>
                                  <span className="text-sm text-gray-900 font-medium">{transaction.description || 'N/A'}</span>
                                </div>
                                <div className="flex flex-col gap-1">
                                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Agent Type:</span>
                                  <span className="text-sm text-gray-900 font-medium">{transaction.agent_type || 'N/A'}</span>
                                </div>
                                <div className="flex flex-col gap-1">
                                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Confidence:</span>
                                  <span className="text-sm text-gray-900 font-medium">{transaction.confidence || 'N/A'}</span>
                                </div>
                                <div className="flex flex-col gap-1 col-span-2">
                                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Reason:</span>
                                  <span className="text-sm text-gray-900 font-medium">{transaction.reason || 'N/A'}</span>
                                </div>
                              </div>
                            </div>

                            {/* Payment Summary Section - Shows if payment was created */}
                            {transaction.payment_summary && (
                              <div className="mb-5 bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-lg border-2 border-emerald-300">
                                <h5 className="m-0 mb-3 text-sm font-semibold uppercase tracking-wide text-emerald-600 flex items-center gap-2">
                                  <span className="text-lg">✅</span> {transaction.payment_summary.payment_type === 'je' ? 'Journal Entry Created' : 'Payment Created'}
                                </h5>
                                <div className="grid grid-cols-2 gap-4">
                                  {/* Payment Link */}
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                                      {transaction.payment_summary.payment_type === 'customer' ? 'Customer Payment #' : transaction.payment_summary.payment_type === 'je' ? 'Journal Entry #' : 'Vendor Payment #'}
                                    </span>
                                    <div className="flex flex-wrap gap-2">
                                      {transaction.payment_summary.payment_docs?.map((doc, idx) => (
                                        <a
                                          key={idx}
                                          href={getNetSuiteUrl(
                                            transaction.payment_summary.payment_type === 'customer' ? 'custpymt' : transaction.payment_summary.payment_type === 'je' ? 'journal' : 'vendpymt',
                                            transaction.payment_summary.payment_ids[idx]
                                          )}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="px-3 py-1.5 bg-emerald-500 text-white rounded-md text-xs font-medium hover:bg-emerald-600 transition-colors no-underline"
                                        >
                                          🔗 {doc || transaction.payment_summary.payment_ids[idx]}
                                        </a>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Total Paid */}
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Total Paid</span>
                                    <span className="text-xl font-bold text-emerald-600">
                                      {Number(transaction.payment_summary.total_paid || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </span>
                                  </div>

                                  {/* Applied Documents */}
                                  <div className="flex flex-col gap-1 col-span-2">
                                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                                      {transaction.payment_summary.payment_type === 'customer' ? 'Invoices Applied' : 'Bills Applied'}
                                    </span>
                                    <div className="flex flex-wrap gap-2 mt-1">
                                      {transaction.payment_summary.applied_docs?.map((doc, idx) => (
                                        <span key={idx} className="px-3 py-1.5 bg-white border border-emerald-200 text-gray-700 rounded-md text-xs font-medium">
                                          {doc}: <span className="text-emerald-600 font-semibold">
                                            {Number(transaction.payment_summary.applied_amounts[idx] || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                          </span>
                                        </span>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Remaining Amount */}
                                  {(() => {
                                    const originalAmount = Math.abs(parseFloat(transaction.amount || transaction.Amount || 0));
                                    const totalPaid = parseFloat(transaction.payment_summary.total_paid || 0);
                                    const remaining = originalAmount - totalPaid;
                                    return remaining > 0.01 ? (
                                      <div className="flex flex-col gap-1 col-span-2 mt-2 pt-3 border-t border-emerald-200">
                                        <span className="text-[11px] font-semibold text-orange-500 uppercase tracking-wide">⚠️ Remaining Amount</span>
                                        <div className="flex items-center gap-4">
                                          <span className="text-lg font-bold text-orange-500">
                                            {remaining.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                          </span>
                                          <span className="text-xs text-gray-500">
                                            (Original: {originalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })} - Paid: {totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2 })})
                                          </span>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex flex-col gap-1 col-span-2 mt-2 pt-3 border-t border-emerald-200">
                                        <span className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wide">✓ Fully Applied</span>
                                        <span className="text-sm text-emerald-600">
                                          All {originalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })} has been applied
                                        </span>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            )}

                            {/* Pending Approval - No payment created yet */}
                            {transaction.has_child_workflow && !transaction.payment_summary && (
                              <div className="mb-5 bg-blue-50 p-4 rounded-lg border-2 border-blue-300">
                                <h5 className="m-0 mb-2 text-sm font-semibold uppercase tracking-wide text-blue-600 flex items-center gap-2">
                                  <span className="text-lg">💬</span> Chat In Progress
                                </h5>
                                <p className="text-sm text-gray-600 m-0">
                                  This transaction has an active chat session. Click "Go to Chat" to continue the approval process.
                                </p>
                              </div>
                            )}

                            {/* Customer/Vendor Details (AR/AP specific) */}
                            {transaction.agent_type === 'bank_agent' && (
                              <div className="mb-5 bg-white p-4 rounded-lg border border-gray-200">
                                <h5 className="m-0 mb-2.5 text-sm font-semibold uppercase tracking-wide text-green-500">Customer Payment Details (AR)</h5>
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Customer Name (Extracted):</span>
                                    <span className="text-sm text-gray-900 font-medium">{transaction.customername || 'N/A'}</span>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">NetSuite Customer Name:</span>
                                    <span className="text-sm text-gray-900 font-medium">{transaction.nscustomername || 'Not Mapped'}</span>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">NetSuite Customer ID:</span>
                                    <span className="text-sm text-gray-900 font-medium">{transaction.nscustomerinternalid || 'Not Mapped'}</span>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Invoice Number:</span>
                                    <span className="text-sm text-gray-900 font-medium">{transaction.invoicenumber || 'N/A'}</span>
                                  </div>

                                  {/* Similar Customers - only show when NOT matched */}
                                  {!transaction.nscustomerinternalid && transaction.similarcustomer && transaction.similarcustomer.length > 0 && (
                                    <div className="flex flex-col gap-1 col-span-2">
                                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Similar Customers Found:</span>
                                      <div className="flex flex-wrap gap-2 mt-2">
                                        {transaction.similarcustomer.map((cust, idx) => (
                                          <span key={idx} className="px-3 py-1.5 bg-indigo-100 text-indigo-800 rounded-md text-xs font-medium">
                                            {typeof cust === 'object' ? `${cust.name || cust.nscustomername || 'Unknown'} (ID: ${cust.internalid || cust.nscustomerinternalid || 'N/A'})` : cust}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Invoice List */}
                                  {transaction.invoicelist && transaction.invoicelist.length > 0 && transaction.invoicelist[0].invoicenumber && (
                                    <div className="flex flex-col gap-1 col-span-2">
                                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Invoice Details:</span>
                                      <div className="flex flex-col gap-2 mt-2">
                                        {transaction.invoicelist.map((inv, idx) => (
                                          <div key={idx} className="p-2.5 bg-gray-100 rounded-md text-[13px] text-gray-700 border-l-[3px] border-indigo-500">
                                            Invoice: {inv.invoicenumber || 'N/A'},
                                            Total: {inv.invoice_total || 'N/A'},
                                            Due: {inv.duedate || 'N/A'}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* NetSuite Invoice Arrays */}
                                  {transaction.nsinvoicedocnum && transaction.nsinvoicedocnum.length > 0 && (
                                    <div className="flex flex-col gap-1 col-span-2">
                                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">NetSuite Invoice Numbers:</span>
                                      <span className="text-sm text-gray-900 font-medium">{transaction.nsinvoicedocnum.join(', ')}</span>
                                    </div>
                                  )}
                                  {transaction.nsinvoiceamount && transaction.nsinvoiceamount.length > 0 && (
                                    <div className="flex flex-col gap-1">
                                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">NetSuite Invoice Amounts:</span>
                                      <span className="text-sm text-gray-900 font-medium">{transaction.nsinvoiceamount.join(', ')}</span>
                                    </div>
                                  )}
                                  {transaction.nsinvoiceinternalid && transaction.nsinvoiceinternalid.length > 0 && (
                                    <div className="flex flex-col gap-1">
                                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">NetSuite Invoice IDs:</span>
                                      <span className="text-sm text-gray-900 font-medium">{transaction.nsinvoiceinternalid.join(', ')}</span>
                                    </div>
                                  )}

                                  {/* Payment IDs */}
                                  {transaction.ns_payment_id && transaction.ns_payment_id.length > 0 && (
                                    <div className="flex flex-col gap-1">
                                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">NetSuite Payment IDs:</span>
                                      <span className="text-sm text-gray-900 font-medium">{transaction.ns_payment_id.join(', ')}</span>
                                    </div>
                                  )}
                                  {transaction.ns_pay_doc && transaction.ns_pay_doc.length > 0 && (
                                    <div className="flex flex-col gap-1">
                                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">NetSuite Payment Docs:</span>
                                      <span className="text-sm text-gray-900 font-medium">{transaction.ns_pay_doc.join(', ')}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {transaction.agent_type === 'bill_agent' && (
                              <div className="mb-5 bg-white p-4 rounded-lg border border-gray-200">
                                <h5 className="m-0 mb-2.5 text-sm font-semibold uppercase tracking-wide text-amber-500">Vendor Payment Details (AP)</h5>
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Vendor Name (Extracted):</span>
                                    <span className="text-sm text-gray-900 font-medium">{transaction.vendorname || 'N/A'}</span>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">NetSuite Vendor Name:</span>
                                    <span className="text-sm text-gray-900 font-medium">{transaction.nsvendorname || 'Not Mapped'}</span>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">NetSuite Vendor ID:</span>
                                    <span className="text-sm text-gray-900 font-medium">{transaction.nsvendorinternalid || 'Not Mapped'}</span>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Bill Number:</span>
                                    <span className="text-sm text-gray-900 font-medium">{transaction.billnumber || 'N/A'}</span>
                                  </div>

                                  {/* Similar Vendors - only show when NOT matched */}
                                  {!transaction.nsvendorinternalid && transaction.similarvendor && transaction.similarvendor.length > 0 && (
                                    <div className="flex flex-col gap-1 col-span-2">
                                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Similar Vendors Found:</span>
                                      <div className="flex flex-wrap gap-2 mt-2">
                                        {transaction.similarvendor.map((vend, idx) => (
                                          <span key={idx} className="px-3 py-1.5 bg-indigo-100 text-indigo-800 rounded-md text-xs font-medium">
                                            {typeof vend === 'object' ? `${vend.name || vend.nsvendorname || 'Unknown'} (ID: ${vend.internalid || vend.nsvendorinternalid || 'N/A'})` : vend}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Bill List */}
                                  {transaction.billlist && transaction.billlist.length > 0 && transaction.billlist[0].billnumber && (
                                    <div className="flex flex-col gap-1 col-span-2">
                                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Bill Details:</span>
                                      <div className="flex flex-col gap-2 mt-2">
                                        {transaction.billlist.map((bill, idx) => (
                                          <div key={idx} className="p-2.5 bg-gray-100 rounded-md text-[13px] text-gray-700 border-l-[3px] border-indigo-500">
                                            Bill: {bill.billnumber || 'N/A'},
                                            Total: {bill.billdate_total || 'N/A'},
                                            Due: {bill.duedate || 'N/A'}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* NetSuite Bill Arrays */}
                                  {transaction.nsbilldocnum && transaction.nsbilldocnum.length > 0 && (
                                    <div className="flex flex-col gap-1 col-span-2">
                                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">NetSuite Bill Numbers:</span>
                                      <span className="text-sm text-gray-900 font-medium">{transaction.nsbilldocnum.join(', ')}</span>
                                    </div>
                                  )}
                                  {transaction.nsbillamount && transaction.nsbillamount.length > 0 && (
                                    <div className="flex flex-col gap-1">
                                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">NetSuite Bill Amounts:</span>
                                      <span className="text-sm text-gray-900 font-medium">{transaction.nsbillamount.join(', ')}</span>
                                    </div>
                                  )}
                                  {transaction.nsbillinternalid && transaction.nsbillinternalid.length > 0 && (
                                    <div className="flex flex-col gap-1">
                                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">NetSuite Bill IDs:</span>
                                      <span className="text-sm text-gray-900 font-medium">{transaction.nsbillinternalid.join(', ')}</span>
                                    </div>
                                  )}

                                  {/* Bill Payment IDs */}
                                  {transaction.ns_billpayment_id && transaction.ns_billpayment_id.length > 0 && (
                                    <div className="flex flex-col gap-1">
                                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">NetSuite Bill Payment IDs:</span>
                                      <span className="text-sm text-gray-900 font-medium">{transaction.ns_billpayment_id.join(', ')}</span>
                                    </div>
                                  )}
                                  {transaction.ns_billpay_doc && transaction.ns_billpay_doc.length > 0 && (
                                    <div className="flex flex-col gap-1">
                                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">NetSuite Bill Payment Docs:</span>
                                      <span className="text-sm text-gray-900 font-medium">{transaction.ns_billpay_doc.join(', ')}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {transaction.agent_type === 'je_agent' && (
                              <div className="mb-5 bg-white p-4 rounded-lg border border-gray-200">
                                <h5 className="m-0 mb-2.5 text-sm font-semibold uppercase tracking-wide text-indigo-500">Journal Entry Details</h5>
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Transaction Type:</span>
                                    <span className="text-sm text-gray-900 font-medium">{transaction.transactiontype}</span>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Amount:</span>
                                    <span className="text-sm text-gray-900 font-medium">{transaction.amount || transaction.Amount}</span>
                                  </div>
                                  <div className="flex flex-col gap-1 col-span-2">
                                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Description:</span>
                                    <span className="text-sm text-gray-900 font-medium">{transaction.description}</span>
                                  </div>
                                  {/* JE Links - handle both ns_je_id and ns_JE_id */}
                                  {((transaction.ns_je_id && transaction.ns_je_id.length > 0) || (transaction.ns_JE_id && transaction.ns_JE_id.length > 0)) && (
                                    <div className="flex flex-col gap-1 col-span-2">
                                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Created Journal Entries:</span>
                                      <div className="flex flex-wrap gap-2">
                                        {(transaction.ns_je_id || transaction.ns_JE_id || []).map((jeId, idx) => (
                                          <a
                                            key={idx}
                                            href={getNetSuiteUrl('journal', jeId)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-3 py-1.5 bg-indigo-500 text-white rounded-md text-xs font-medium hover:bg-indigo-600 transition-colors no-underline"
                                          >
                                            🔗 {(transaction.ns_je_doc || transaction.ns_JE_doc)?.[idx] || `JE ${jeId}`}
                                          </a>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Additional Fields (shown for all types) */}
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                              <h5 className="m-0 mb-2.5 text-sm font-semibold uppercase tracking-wide text-gray-500">Additional Information</h5>
                              <div className="grid grid-cols-2 gap-3">
                                {transaction.bank && (
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Bank Account:</span>
                                    <span className="text-sm text-gray-900 font-medium">{transaction.bank}</span>
                                  </div>
                                )}
                                <div className="flex flex-col gap-1 col-span-2">
                                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Raw Transaction Data:</span>
                                  <pre className="bg-gray-800 text-green-400 p-4 rounded-md text-xs overflow-x-auto mt-2 font-mono max-h-[300px] overflow-y-auto">
                                    {JSON.stringify(transaction, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer Actions */}
        <div className="px-8 py-5 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
          <button
            onClick={onClose}
            className="bg-white text-gray-700 border-2 border-gray-200 px-6 py-2.5 rounded-md font-semibold cursor-pointer transition-all hover:bg-gray-50 hover:border-gray-300"
          >
            {isLiveWorkflow ? 'Save Draft' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionsModal;
